import React, { useState, useEffect } from 'react';
import { Row, Col, Form, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import Card from '../../../components/Card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Checkbox } from 'primereact/checkbox';
import { toast } from 'react-toastify';
import Loader from '../../../components/partials/components/loader';
import RenderDeleteModal from '../../../components/partials/components/delete-modal';
import { EditIcon, DeleteIcon } from '../../../components/setting/elements';
import { getApiCall, postApiCall } from '../../../service/httpCall';
import { API_CALL_SCRIPT_LIST, API_CALL_SCRIPT_DELETE } from '../../../service/url';
import { ROUTES, MODULE_ACL, USER_ROLES } from '../../../utilities/constant';
import { useAuth } from '../auth/auth-utils';
import { getUser } from '../../../utilities/setting';
import { exportToCSV, hasPermission } from '../../../utilities/helpers';

const CallScriptList = () => {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();

  const [dataList, setDataList]                   = useState([]);
  const [originalData, setOriginalData]           = useState([]);
  const [loading, setLoading]                     = useState(false);
  const [selectedItems, setSelectedItems]         = useState([]);
  const [searchValue, setSearchValue]             = useState('');
  const [enableDeleteModal, setEnableDeleteModal] = useState(false);
  const [confirmDelete, setConfirmDelete]         = useState(false);
  const [deleteId, setDeleteId]                   = useState(null);
  const [isMultiDelete, setIsMultiDelete]         = useState(false);
  const [isSuperAdmin, setIsSuperAdmin]           = useState(false);
  const [loggedInUserACL, setLoggedInUserACL]     = useState({});

  useEffect(() => {
    setLoading(true);
    const user = getUser();
      if (user.role === USER_ROLES.SUPERADMIN) setIsSuperAdmin(true);
      if (user?.acl_settings)
        setLoggedInUserACL(JSON.parse(user.acl_settings.module_operations));
    fetchData();
  }, []);

  const fetchData = () => {
    getApiCall(API_CALL_SCRIPT_LIST)
      .then((resp) => {
        const list = Array.isArray(resp) ? resp : resp?.data ?? [];
        const updated = list.map((val, idx) => ({ ...val, key: idx + 1 }));
        setDataList(updated);
        setOriginalData(updated);
      })
      .catch((err) => toast.error(JSON.stringify(err) || 'Failed to fetch scripts'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (searchValue.trim()) {
      const filtered = originalData.filter(
        (item) =>
          item.title?.toLowerCase().includes(searchValue.toLowerCase()) ||
          item.short_description?.toLowerCase().includes(searchValue.toLowerCase()) ||
          item.description?.toLowerCase().includes(searchValue.toLowerCase())
      );
      setDataList(filtered);
    } else {
      setDataList(originalData);
    }
  }, [searchValue, originalData]);

  useEffect(() => {
    if (!confirmDelete) return;

    const deleteOne = (id) => postApiCall(API_CALL_SCRIPT_DELETE, { id });

    const promise = isMultiDelete
      ? Promise.all(selectedItems.map(deleteOne))
      : deleteOne(deleteId);

    promise
      .then(() => {
        toast.success(isMultiDelete ? 'Selected scripts deleted' : 'Script deleted');
        fetchData();
        setSelectedItems([]);
      })
      .catch((err) => toast.error(JSON.stringify(err) || 'Delete failed'))
      .finally(() => {
        setConfirmDelete(false);
        setEnableDeleteModal(false);
      });
  }, [confirmDelete]);

  const handleSelectAll = (e) =>
    setSelectedItems(e.checked ? dataList.map((item) => item.id) : []);

  const actionTemplate = (rowData) => (
    <div className="d-flex align-items-center list-user-action">
      {(loggedInUserACL[MODULE_ACL.CALL_SCRIPT]?.edit) && (
        <EditIcon linkTo={`/call-scripts/edit/${rowData.id}`} />
      )}
      <div style={{ marginLeft: '10px' }}>
        {(loggedInUserACL[MODULE_ACL.CALL_SCRIPT]?.delete) &&
          selectedItems.length === 0 && (
            <DeleteIcon
              handleCallback={() => {
                setDeleteId(rowData.id);
                setIsMultiDelete(false);
                setEnableDeleteModal(true);
              }}
            />
          )}
      </div>
    </div>
  );

  const [moduleOperationsForReadPermissionCheck, setModuleOperationsForReadPermissionCheck] = useState({});
       
         useEffect(() => {
           const user = getUser();
             if (user) {
               if (user?.acl_settings) {    
                 setModuleOperationsForReadPermissionCheck(JSON.parse(user.acl_settings.module_operations));
               }
             }
         }, []);

  const canRead = hasPermission(
       moduleOperationsForReadPermissionCheck,
       "IVR",
       "read",
     );
   
     if (Object.keys(moduleOperationsForReadPermissionCheck).length > 0 && !canRead) {
       return (
         <div
           className="text-center p-4"
           style={{
             marginTop: "50px",
             marginBottom: "50px",
             fontSize: "16px",
           }}
         >
           No read permission found in assigned ACL
         </div>
       );
     }

  if (loading) return <Loader />;

  return (
    <>
      <Row>
        <Col sm="12">
          <Card>
            <Card.Header className="d-flex justify-content-between">
              <h4 className="card-title">Call Script List</h4>
              {(loggedInUserACL[MODULE_ACL.CALL_SCRIPT]?.add) && (
                <Button onClick={() => navigate(`/${ROUTES.CALL_SCRIPT.CREATE}`)}>
                  Create Script
                </Button>
              )}
            </Card.Header>

            <Card.Body>
              <div className="d-flex mb-3">
                <Button
                  variant="danger"
                  className="me-2"
                  disabled={selectedItems.length === 0}
                  onClick={() => { setIsMultiDelete(true); setEnableDeleteModal(true); }}
                >
                  Delete Selected
                </Button>
                <Form.Control
                  className="w-25"
                  placeholder="Search scripts..."
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                />
              </div>

              <DataTable value={dataList} paginator rows={10} style={{ width: '100%' }}>

                <Column
                  header={
                    <Checkbox
                      checked={selectedItems.length === dataList.length && dataList.length > 0}
                      onChange={handleSelectAll}
                    />
                  }
                  body={(rd) => (
                    <Checkbox
                      checked={selectedItems.includes(rd.id)}
                      onChange={() =>
                        setSelectedItems((prev) =>
                          prev.includes(rd.id)
                            ? prev.filter((i) => i !== rd.id)
                            : [...prev, rd.id]
                        )
                      }
                    />
                  )}
                  style={{ width: '50px' }}
                />

                <Column field="title" header="Title" style={{ minWidth: '150px' }} />

                <Column
                  field="short_description"
                  header="Short Description"
                  style={{ maxWidth: '200px' }}
                />

                <Column
  field="description"
  header="Description"
  body={(rowData) => (
    <div
      style={{
        maxWidth: "350px",
        maxHeight: "80px",
        overflowY: "auto",
        overflowX: "hidden",
        wordBreak: "break-word",
        whiteSpace: "normal",
        paddingRight: "6px",
      }}
      dangerouslySetInnerHTML={{
        __html: rowData.description || "-"
      }}
    />
  )}
/>

                <Column header="Action" body={actionTemplate} style={{ width: '100px' }} />

              </DataTable>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <RenderDeleteModal
        show={enableDeleteModal}
        setShow={setEnableDeleteModal}
        setConfirmDelete={setConfirmDelete}
        isMultiDelete={isMultiDelete}
      />
    </>
  );
};

export default CallScriptList;